import clsx from 'clsx'
import { useRouter } from 'next/router'

import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'
import type { BadgeProps } from '@wartek-id/badge'
import { Badge } from '@wartek-id/badge'
import { Button } from '@wartek-id/button-v1'

type HeaderProps = {
  title: string | React.ReactNode
  subtitle?: string | React.ReactNode
  titleId?: string
  rightNode?: React.ReactNode
  hideOnPrint?: boolean
  className?: string
  badge?: {
    text: string
    variant?: BadgeProps['variant']
  }
  onBack?: () => void
}

export function Header({
  className,
  title,
  subtitle,
  titleId,
  hideOnPrint = false,
  rightNode = null,
  badge,
  onBack,
}: HeaderProps) {
  const router = useRouter()

  const handleBack = () => {
    if (typeof onBack === 'function') {
      onBack()
    } else {
      router.back()
    }
  }

  return (
    <div className={clsx('flex w-full items-center space-x-5', className)}>
      <div
        className={clsx(
          'min-w-96',
          'flex flex-1 items-center gap-3',
          hideOnPrint && 'print:hidden'
        )}
      >
        <Button
          type="button"
          variant="ghost"
          iconOnly
          className={clsx('flex-none', subtitle ? 'self-start' : 'self-center')}
          onClick={handleBack}
          aria-label="Kembali ke halaman sebelumnya"
        >
          {/*
           * Note: we need to explicitly set font-size and line-height as workaround of styles collision
           * due to multiple version of @wartek-id/icon being used in this project.
           */}
          <Icon
            className="text-black !text-4xl !leading-9 print:hidden select-none"
            fontSize="lg"
          >
            arrow_back
          </Icon>
        </Button>
        <div className="space-y-1">
          <Text
            id={titleId}
            variant="display-md"
            className="text-black pointer-events-none select-none"
            as="h1"
          >
            {title}
          </Text>
          {subtitle && <Text variant="body">{subtitle}</Text>}
        </div>
        {badge?.text && (
          <Badge variant={badge?.variant ?? 'neutral'}>{badge.text}</Badge>
        )}
      </div>
      {rightNode}
    </div>
  )
}
